import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, Video, Wand2, RotateCw, Download, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Vendor-side "Generate AI ad video" button + dialog.
 *
 * Lets a vendor select one of their products, format, duration and mood,
 * then triggers Sora-2 video generation. Polls until ready and renders a player.
 */
export const AiVideoGenerator = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);

  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [form, setForm] = useState({
    product_id: '',
    size: '1280x720',
    duration: 8,
    mood: 'cinématique et premium',
  });
  const [lastResult, setLastResult] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [v, s, p] = await Promise.all([
        axios.get(`${API}/ai/videos`, { headers }),
        axios.get(`${API}/subscriptions/me`, { headers }).catch(() => null),
        axios.get(`${API}/vendors/me`, { headers }).then((r) => axios.get(`${API}/vendors/${r.data.id}/products?limit=50`)),
      ]);
      setVideos(v.data || []);
      if (s?.data) setSubscription(s.data);
      setProducts(p.data || []);
      if ((p.data || []).length > 0 && !form.product_id) {
        setForm((f) => ({ ...f, product_id: p.data[0].id }));
      }
    } catch (_) { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => { if (token) fetchData(); /* eslint-disable-next-line */ }, [token]);

  const hasAddon = !!subscription?.video_addon;
  const quota = subscription?.videos_quota ?? 0;
  const used = subscription?.videos_used_this_month ?? 0;
  const quotaLabel = quota == null ? '∞' : quota;

  const generate = async () => {
    if (!form.product_id) {
      toast.error(T('Sélectionnez un produit', 'Select a product'));
      return;
    }
    setGenerating(true);
    setLastResult(null);
    toast.info(T('Génération en cours… cela prend 2 à 5 minutes', 'Generating… this takes 2 to 5 minutes'));
    try {
      const r = await axios.post(
        `${API}/ai/generate-ad-video`,
        { product_id: form.product_id, size: form.size, duration: form.duration, mood: form.mood, language },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 600000 }
      );
      setLastResult(r.data);
      toast.success(T('Vidéo générée !', 'Video ready!'));
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || T('Erreur', 'Error');
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  const videoSrc = (v) => `${API}/ai/videos/${v.id}?auth=${encodeURIComponent(token)}`;

  return (
    <>
      <Button onClick={() => setOpen(true)} className="btn-primary" data-testid="ai-video-open-btn">
        <Wand2 className="w-4 h-4 mr-2" />
        {T('Générer une vidéo pub IA', 'Generate AI ad video')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#E9C46A]" />
              {T('Studio vidéo IA', 'AI Video Studio')}
            </DialogTitle>
            <DialogDescription>
              {T(
                'Script généré par Gemini, vidéo produite par Sora 2 — 2 à 5 minutes par génération.',
                'Script generated by Gemini, video produced by Sora 2 — 2 to 5 minutes per generation.'
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Subscription status */}
          {!hasAddon ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="no-addon-warning">
              <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">
                  {T('Add-on IA Vidéo non activé', 'AI Video add-on not active')}
                </p>
                <p className="text-xs text-amber-800 mt-1">
                  {T(
                    'Souscrivez à l\'add-on AI Vidéo Lite (19€/mois - 10 vidéos) ou Illimité (49€/mois) depuis la page tarifs.',
                    'Subscribe to AI Video Lite (€19/mo - 10 videos) or Unlimited (€49/mo) from the pricing page.'
                  )}
                </p>
                <Button asChild variant="outline" className="mt-3" size="sm">
                  <a href="/pricing">{T('Voir les tarifs →', 'View pricing →')}</a>
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-[#FBF8F3] rounded-xl p-3 text-sm text-[#0F4C5C] flex items-center justify-between">
              <span><strong>{subscription.video_addon.name}</strong> · {used} / {quotaLabel} {T('vidéos ce mois', 'videos this month')}</span>
              <Sparkles className="w-4 h-4 text-[#E9C46A]" />
            </div>
          )}

          {/* Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="md:col-span-2">
              <Label>{T('Produit', 'Product')}</Label>
              <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })} disabled={generating}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="ai-product-select">
                  <SelectValue placeholder={T('Sélectionner un produit', 'Select a product')} />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name_fr || p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{T('Format', 'Format')}</Label>
              <Select value={form.size} onValueChange={(v) => setForm({ ...form, size: v })} disabled={generating}>
                <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1280x720">{T('Paysage HD (16:9)', 'Landscape HD (16:9)')}</SelectItem>
                  <SelectItem value="1024x1792">{T('Portrait (9:16) — Reels/TikTok', 'Portrait (9:16) — Reels/TikTok')}</SelectItem>
                  <SelectItem value="1024x1024">{T('Carré (1:1) — Instagram', 'Square (1:1) — Instagram')}</SelectItem>
                  <SelectItem value="1792x1024">{T('Cinéma large', 'Widescreen')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{T('Durée', 'Duration')}</Label>
              <Select value={String(form.duration)} onValueChange={(v) => setForm({ ...form, duration: parseInt(v, 10) })} disabled={generating}>
                <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">4 {T('secondes', 'seconds')}</SelectItem>
                  <SelectItem value="8">8 {T('secondes', 'seconds')}</SelectItem>
                  <SelectItem value="12">12 {T('secondes', 'seconds')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>{T('Ambiance / Style', 'Mood / Style')}</Label>
              <Input
                value={form.mood}
                onChange={(e) => setForm({ ...form, mood: e.target.value })}
                placeholder={T('Ex: minimaliste, luxueux, ludique, naturel...', 'E.g. minimalist, luxurious, playful, natural...')}
                className="mt-1.5 h-11"
                disabled={generating}
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={generating}>
              {T('Fermer', 'Close')}
            </Button>
            <Button
              onClick={generate}
              disabled={generating || !hasAddon || !form.product_id}
              className="btn-primary"
              data-testid="ai-generate-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
              {generating ? T('Génération en cours…', 'Generating…') : T('Générer la vidéo', 'Generate video')}
            </Button>
          </DialogFooter>

          {/* Last result */}
          {lastResult && lastResult.status === 'ready' && (
            <div className="mt-6 bg-[#0F4C5C] text-white rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold flex items-center gap-2"><Video className="w-4 h-4 text-[#E9C46A]" /> {T('Vidéo générée', 'Generated video')}</h3>
                <a href={`${API}/ai/videos/${lastResult.id}?auth=${encodeURIComponent(token)}`} download className="text-xs underline hover:text-[#E9C46A]">
                  <Download className="w-3.5 h-3.5 inline mr-1" /> {T('Télécharger', 'Download')}
                </a>
              </div>
              <video controls className="w-full rounded-xl bg-black" src={`${API}/ai/videos/${lastResult.id}?auth=${encodeURIComponent(token)}`}>
                {T('Votre navigateur ne supporte pas la vidéo.', 'Your browser does not support video.')}
              </video>
              <details className="mt-3">
                <summary className="text-xs text-white/70 cursor-pointer">{T('Script utilisé', 'Script used')}</summary>
                <p className="text-xs text-white/80 mt-2 leading-relaxed italic">"{lastResult.script}"</p>
              </details>
            </div>
          )}

          {/* History */}
          {videos.length > 0 && (
            <div className="mt-6">
              <h3 className="font-bold text-[#0F4C5C] mb-3">{T('Mes vidéos récentes', 'My recent videos')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {videos.slice(0, 6).map((v) => (
                  <div key={v.id} className="bg-[#FBF8F3] rounded-xl overflow-hidden border border-[#0F4C5C]/10">
                    {v.status === 'ready' ? (
                      <video controls className="w-full aspect-video bg-black" src={videoSrc(v)} />
                    ) : (
                      <div className="w-full aspect-video flex items-center justify-center bg-slate-900 text-white text-xs">
                        {v.status === 'generating' ? <Loader2 className="w-5 h-5 animate-spin" /> : v.status}
                      </div>
                    )}
                    <div className="p-2 text-xs">
                      <p className="font-semibold text-[#0F4C5C] truncate">{v.product_name}</p>
                      <p className="text-[#0F4C5C]/60">{v.size} · {v.duration}s</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
